import { ApolloClient, gql } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { DATE_PARSE_FORMAT, DatePickerValue } from '@aleph/shared-components';
import dayjs from 'dayjs';
import { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';

type ReportParams = {
	client: ApolloClient<any>
	filters: Record<string, any>,
	breakdowns: string[]
	metrics: string[]
	dates: DatePickerValue
};

/** Display order of breakdowns as shown to the user (Date, Ad, Group, Campaign, Ad account)
 * packages/wb-spotify/src/app/routes/utils.ts SPOTIFY_REPORT_BREAKDOWN_OPTIONS_CATEGORIZED and
 * SPOTIFY_REPORT_BREAKDOWN_OPTIONS_LIFETIME
 * */
const BREAKDOWN_DISPLAY_ORDER = ['adName', 'groupName', 'campaignName', 'adAccountName'] as const;

const BREAKDOWN_TO_ID_FIELD: Record<string, string> = {
	adName:        'externalAdId',
	groupName:     'externalGroupId',
	campaignName:  'externalCampaignId',
	adAccountName: 'adAccountId',
};

const getFirstBreakdownIdField = (breakdowns: string[]): string | null => {
	const firstInDisplayOrder = BREAKDOWN_DISPLAY_ORDER.find(
		b => breakdowns.includes(b),
	);

	return firstInDisplayOrder ? BREAKDOWN_TO_ID_FIELD[firstInDisplayOrder] ?? null : null;
};

type GetReportQueryParams = {
	breakdowns: string[]
	metrics: string[]
	extraIdField?: string | null
};

const getReportQuery = ({ breakdowns, metrics, extraIdField }: GetReportQueryParams): DocumentNode => gql`
	query reportStats ($filters: StatsFilter, $cursor: String) {
		stats(filters: $filters, cursor: $cursor) {
			items {
				${breakdowns.join('\n')}
				${extraIdField ? `date\n${extraIdField}\n` : ''}
				${metrics.join('\n')}
			}
			cursor
		}
	}`;

const saveCursor = (cursor: string | null) => {
	if (cursor) {
		sessionStorage.setItem('create_reports_cursor', cursor);
	}
};

const retrieveCursor = () => sessionStorage.getItem('create_reports_cursor') ?? null;

export const getSpotifyReportsDatasource = (reportParams: ReportParams): IServerSideDatasource => ({
	getRows: async (params: IServerSideGetRowsParams) => {
		try {
			params.api.hideOverlay();

			if (reportParams.metrics.length === 0 || reportParams.breakdowns.length === 0) {
				params.api.showNoRowsOverlay();
				params.success({
					rowData:  [],
					rowCount: 0,
				});

				return;
			}

			const variables = {
				cursor:  params.request.startRow !== 0 ? retrieveCursor() : null,
				filters: {
					...reportParams.filters,
					date: {
						value: `${dayjs(reportParams?.dates?.from).format(DATE_PARSE_FORMAT)}:${dayjs(reportParams?.dates?.to).format(DATE_PARSE_FORMAT)}`,
					},
				},
			};

			if (useSupergridSettings.getState().lifetimeStats) {
				// @ts-ignore
				delete variables.filters.date;
			}

			const hasDateBreakdown = reportParams.breakdowns.includes('date');
			const extraIdField = !hasDateBreakdown
				? getFirstBreakdownIdField(reportParams.breakdowns)
				: null;

			const { data } = await reportParams.client.query({
				query: getReportQuery({
					breakdowns: reportParams.breakdowns,
					metrics:    reportParams.metrics,
					extraIdField,
				}),
				fetchPolicy: 'no-cache',
				variables,
			});

			const rowData = data?.stats?.items ?? [];
			const cursor = data?.stats?.cursor;

			saveCursor(cursor);

			if (rowData.length === 0 && params.request.startRow === 0) {
				params.api.showNoRowsOverlay();
			}

			params.success({
				rowData,
				rowCount: cursor ? undefined : params.request.startRow + rowData.length,
			});
		} catch (e) {
			console.error(e);
			params.fail();
		}
	},
});
